/**
 * WorldDecorationsBuilder.ts — populates the static-mesh pass with
 * trees and buildings derived from the active terrain semantics.
 *
 * Lifted out of `main.ts` so all the procedural-asset wiring,
 * deterministic-jitter helpers and matrix bookkeeping live in one
 * place. Pure build-time work — no per-frame state — so the
 * orchestrator just calls {@link build} once after the static-mesh
 * pass exists and forgets about it.
 *
 * Three phases run in order:
 *  1. Trees: variants + per-position picking come from
 *     {@link DecorationsLibrary}, which loads the GLB pack defined in
 *     `/decorations/index.json`. Variant choice is a deterministic hash
 *     of (x, z) so reloads keep the same forest.
 *  2. Building footprints: connected-component PCA-aligned OBBs
 *     extruded to walls + a steep gable roof along the longer axis.
 *     Wall height + roof pitch jitter deterministically per building.
 */
import {
  createBox,
  createCylinderY,
  createGableRoof,
  createWindsockTubeX,
} from "../assets/ProceduralAssets";
import { loadGlb, type GltfMeshAsset } from "../assets/GltfLoader";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import { extractBuildingsFromAnnotation } from "./building-extrude";
import { scatterTreesFromTerrainAnnotations } from "./tree-scatter";
import {
  sampleTerrainHeight,
  sampleTerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import { srgbHexToLinear } from "../rendering/LightingUniforms";
import { DecorationsLibrary } from "./DecorationsLibrary";

type Color = [number, number, number];

/** Building palette — warm wall + dark red roof. */
const WALL_COLOR: Color = [0.62, 0.45, 0.32];
const ROOF_COLOR: Color = [0.42, 0.18, 0.14];
const ANNOTATION_HOUSE_MODELS = [
  {
    id: "house_letter_t_blue",
    url: "/buildings/house_letter_t_blue.glb",
    wallMaterial: "House 1",
    roofMaterials: ["Gray House"],
  },
  {
    id: "house_2_floors",
    url: "/buildings/house_2_floors.glb",
    wallMaterial: "House",
    roofMaterials: ["hOUSE rOOF"],
  },
] as const;
const ANNOTATION_HOUSE_SCHEMES: Array<{ wall: Color; roof: Color }> = [
  { wall: srgbHexToLinear("#B4573D"), roof: srgbHexToLinear("#3F4144") },
  { wall: srgbHexToLinear("#EFE8D8"), roof: srgbHexToLinear("#252629") },
  { wall: srgbHexToLinear("#CFCFC8"), roof: srgbHexToLinear("#73777A") },
  { wall: srgbHexToLinear("#E6D4B4"), roof: srgbHexToLinear("#8C3F2F") },
  { wall: srgbHexToLinear("#F2EEE4"), roof: srgbHexToLinear("#55585B") },
  { wall: srgbHexToLinear("#C9825B"), roof: srgbHexToLinear("#2D2F32") },
  { wall: srgbHexToLinear("#D8C3A1"), roof: srgbHexToLinear("#696D70") },
  { wall: srgbHexToLinear("#A84835"), roof: srgbHexToLinear("#6F2E25") },
];
const WINDSOCK_POLE_ID = "windsock/pole";
const WINDSOCK_CLOTH_ID = "windsock/cloth";
const WINDSOCK_POLE_HEIGHT_M = 6.5;
const WINDSOCK_CLOTH_HEIGHT_M = 6.15;

/** Build a column-major mat4: T * Ry(yaw) * S(sx, sy, sz). */
function trsY(
  sx: number,
  sy: number,
  sz: number,
  yaw: number,
  tx: number,
  ty: number,
  tz: number,
): Mat4 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  // prettier-ignore
  return new Float32Array([
    c * sx, 0, -s * sx, 0,
    0,      sy, 0,      0,
    s * sz, 0, c * sz,  0,
    tx,     ty, tz,     1,
  ]) as Mat4;
}

/**
 * Cheap deterministic [0,1) hash of an (x, z) pair, used to keep
 * per-instance jitter (variant choice, height, pitch) stable across
 * page reloads.
 */
function hashJitter(x: number, z: number): number {
  const h = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return h - Math.floor(h);
}

/** Stable 32-bit-ish integer seed from a layer name (used to decorrelate
 *  the per-layer scatter grids). */
function layerSeed(name: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h || 1;
}

export class WorldDecorationsBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  /**
   * Spawn trees + buildings from the supplied terrain. No-op if the
   * terrain has no annotations data.
   */
  async build(terrain: TerrainAsset): Promise<void> {
    await this.scatterTrees(terrain);
    await this.placeAnnotationBuildings(terrain);
    this.placeAnnotationWindsocks(terrain);
  }

  private placeAnnotationWindsocks(terrain: TerrainAsset): void {
    const markers = extractWindsockMarkers(terrain);
    if (markers.length === 0) return;

    this.staticMeshPass.addGroup(
      WINDSOCK_POLE_ID,
      createCylinderY(
        0.055,
        WINDSOCK_POLE_HEIGHT_M,
        16,
        [0.72, 0.74, 0.76],
        WINDSOCK_POLE_ID,
        true,
      ),
      0,
      { castsShadow: true, receivesShadow: true },
    );
    this.staticMeshPass.addGroup(
      WINDSOCK_CLOTH_ID,
      createWindsockTubeX(
        3.4,
        0.48,
        0.24,
        10,
        18,
        [
          [0.95, 0.05, 0.04],
          [0.95, 0.05, 0.04],
          [0.96, 0.92, 0.88],
          [0.96, 0.92, 0.88],
          [0.95, 0.05, 0.04],
          [0.95, 0.05, 0.04],
          [0.96, 0.92, 0.88],
          [0.96, 0.92, 0.88],
          [0.95, 0.05, 0.04],
          [0.95, 0.05, 0.04],
        ],
        WINDSOCK_CLOTH_ID,
      ),
      2,
      { castsShadow: true, receivesShadow: false },
    );

    for (const marker of markers) {
      this.staticMeshPass.addInstance(
        WINDSOCK_POLE_ID,
        trsY(1, 1, 1, 0, marker.x, marker.y, marker.z),
      );
      this.staticMeshPass.addInstance(
        WINDSOCK_CLOTH_ID,
        trsY(
          1,
          1,
          1,
          0,
          marker.x,
          marker.y + WINDSOCK_CLOTH_HEIGHT_M,
          marker.z,
        ),
      );
    }
    console.log(`[Windsock] placed ${markers.length} marker windsock(s)`);
  }

  private async scatterTrees(terrain: TerrainAsset): Promise<void> {
    // Load the configured vegetation pack. Each enabled GLB is one
    // StaticMeshPass group; multiple layers (tree / bush / flower /
    // stone / stump / log / mushroom) are scattered independently so
    // small props don't compete with canopy density.
    let lib: DecorationsLibrary;
    try {
      lib = await DecorationsLibrary.load(
        this.staticMeshPass,
        terrain.meta.vegetation_biome ?? "default",
        "/decorations/index.json",
        terrain.meta.vegetation_tree_mix,
      );
    } catch (err) {
      console.warn("[Vegetation] pack failed to load, skipping scatter", err);
      return;
    }

    // Layer order is fixed (cosmetic only): big things first so logs
    // scatter calls which trigger console.log appear in a sensible order.
    const LAYERS = [
      "tree",
      "bush",
      "stump",
      "log",
      "stone",
      "flower",
      "mushroom",
    ] as const;

    let totalPlaced = 0;
    for (const type of LAYERS) {
      const layer = lib.layer(type);
      if (!layer || layer.maxCount <= 0) continue;
      // Per-layer seed so the deterministic grids of different layers
      // don't all collapse onto the same cells.
      const seed = layerSeed(type);
      const placements = scatterTreesFromTerrainAnnotations(terrain, {
        cellM: layer.cellM,
        density:
          type === "tree"
            ? (terrain.meta.vegetation_tree_density ?? layer.density)
            : layer.density,
        maxCount: layer.maxCount,
        seed,
        minGroundM: layer.minGroundM,
        shoreRingM: layer.shoreRingM,
        sinkM: layer.sinkM,
        nearShoreM: layer.nearShoreM,
        allowedSurfaces:
          type === "tree" ? terrain.meta.vegetation_tree_surfaces : undefined,
      });
      let placed = 0;
      for (const p of placements) {
        const v =
          type === "tree"
            ? lib.pickTree(p.x, p.z, sampleTerrainSurface(terrain, p.x, p.z))
            : lib.pickProp(type, p.x, p.z);
        if (!v) continue;
        // Per-instance scale jitter centred on 1.0. When `targetHeightM`
        // is set the layer wants a specific world-space size — convert
        // that to a uniform scale via the model's authored bbox height.
        // Otherwise the scale is just `1 ± jitter` (artist intent).
        const j = hashJitter(p.x + seed, p.z + seed * 1.31);
        const jitterMul = 1 - layer.scaleJitter * 0.5 + j * layer.scaleJitter;
        const scale =
          layer.targetHeightM != null
            ? (layer.targetHeightM * jitterMul) / v.modelHeight
            : jitterMul;
        // Anchor the model's base on the sampled ground regardless of
        // its local-space pivot.
        const ty = p.y - v.modelMinY * scale;
        // X/Z pivot compensation: many GLBs have their authored origin
        // off-centre (e.g. trunk modelled at local +2 on X). Without this
        // the visible trunk drifts by a constant offset. Subtract the
        // model's bbox centre, rotated by yaw, from the placement point.
        const cy = Math.cos(p.yaw);
        const sy = Math.sin(p.yaw);
        const ox = (v.modelCenterX * cy + v.modelCenterZ * sy) * scale;
        const oz = (-v.modelCenterX * sy + v.modelCenterZ * cy) * scale;
        const tx = p.x - ox;
        const tz = p.z - oz;
        this.staticMeshPass.addInstance(
          v.meshId,
          trsY(scale, scale, scale, p.yaw, tx, ty, tz),
        );
        placed++;
      }
      totalPlaced += placed;
      console.log(
        `[Vegetation] layer "${type}": placed ${placed} / ${placements.length} (cap ${layer.maxCount})`,
      );
    }
    console.log(`[Vegetation] total instances placed: ${totalPlaced}`);
  }

  private async placeAnnotationBuildings(terrain: TerrainAsset): Promise<void> {
    const annBuildings = extractBuildingsFromAnnotation(terrain);
    if (annBuildings.length === 0) return;

    try {
      const variants = await Promise.all(
        ANNOTATION_HOUSE_MODELS.flatMap((model) =>
          ANNOTATION_HOUSE_SCHEMES.map(async (scheme, schemeIndex) => {
            const materialColors: Record<string, Color> = {
              [model.wallMaterial]: scheme.wall,
            };
            for (const roofMaterial of model.roofMaterials) {
              materialColors[roofMaterial] = scheme.roof;
            }
            const mesh = await loadGlb(model.url, materialColors);
            const meshId = `building/${model.id}/${schemeIndex}`;
            this.staticMeshPass.addGroup(meshId, mesh, 0, {
              castsShadow: true,
              castsHardShadow: true,
              receivesShadow: true,
            });
            return { meshId, mesh };
          }),
        ),
      );
      this.placeAnnotationBuildingModels(annBuildings, variants);
      console.log(
        `[Buildings] placed ${annBuildings.length} GLB house(s) from annotations (${ANNOTATION_HOUSE_MODELS.length} models, ${ANNOTATION_HOUSE_SCHEMES.length} colour schemes)`,
      );
      return;
    } catch (err) {
      console.warn(
        "[Buildings] failed to load annotation building GLBs, using procedural fallback",
        err,
      );
    }

    this.extrudeAnnotationBuildings(annBuildings);
  }

  private placeAnnotationBuildingModels(
    annBuildings: ReturnType<typeof extractBuildingsFromAnnotation>,
    variants: Array<{ meshId: string; mesh: GltfMeshAsset }>,
  ): void {
    const first = variants[0];
    if (!first) return;
    const min = first.mesh.bbox.min;
    const max = first.mesh.bbox.max;
    const modelCenterX = (min[0] + max[0]) * 0.5;
    const modelCenterZ = (min[2] + max[2]) * 0.5;

    for (const building of annBuildings) {
      const variantIndex = Math.min(
        variants.length - 1,
        Math.floor(
          hashJitter(building.x + 19.7, building.z - 4.3) * variants.length,
        ),
      );
      const variant = variants[variantIndex] ?? first;
      const cy = Math.cos(building.yaw);
      const syaw = Math.sin(building.yaw);
      const ox = modelCenterX * cy + modelCenterZ * syaw;
      const oz = -modelCenterX * syaw + modelCenterZ * cy;
      this.staticMeshPass.addInstance(
        variant.meshId,
        trsY(
          1,
          1,
          1,
          building.yaw,
          building.x - ox,
          building.y - min[1],
          building.z - oz,
        ),
      );
    }
  }

  private extrudeAnnotationBuildings(
    annBuildings: ReturnType<typeof extractBuildingsFromAnnotation>,
  ): void {
    this.staticMeshPass.addGroup(
      "annBuildingWall",
      createBox(1, 1, 1, WALL_COLOR, "annBuildingWall"),
    );
    // Gable roof: triangular prism with ridge along +X (unit space).
    this.staticMeshPass.addGroup(
      "annBuildingRoof",
      createGableRoof(ROOF_COLOR, "annBuildingRoof"),
    );

    for (const b of annBuildings) {
      // 1-storey + attic look: walls are ground-floor only (2.6–3.4 m,
      // jittered ±0.4 m around the preset eaves height), then a steep
      // gable on top adds the attic volume.
      const eavesBase = b.height;
      const wallH = eavesBase + (hashJitter(b.x, b.z) - 0.5) * 0.8;
      this.staticMeshPass.addInstance(
        "annBuildingWall",
        trsY(b.sx, wallH, b.sz, b.yaw, b.x, b.y + wallH * 0.5, b.z),
      );

      // Place ridge along the LONGER footprint axis. b.yaw aligns
      // local +X with the major (sx) axis, so when sx >= sz we use
      // yaw as-is; if somehow sz > sx (rare given OBB convention)
      // rotate 90° so ridge still runs along the longer side.
      const ridgeAlongSx = b.sx >= b.sz;
      const ridgeLen = ridgeAlongSx ? b.sx : b.sz;
      const eaveLen = ridgeAlongSx ? b.sz : b.sx;
      const ridgeYaw = ridgeAlongSx ? b.yaw : b.yaw + Math.PI / 2;

      // Steep attic pitch ~40°: gable height = 0.42 × eave-to-eave,
      // jittered ±0.04 per building so the row stays lively.
      const pitchJitter = 0.38 + hashJitter(b.x + 11, b.z + 7) * 0.08;
      const roofH = eaveLen * pitchJitter;
      // Overhang on every side — same 0.5 m on long sides AND gable
      // ends so the roof clearly sticks out past the walls.
      const overhang = 0.5;
      this.staticMeshPass.addInstance(
        "annBuildingRoof",
        trsY(
          ridgeLen + overhang * 2,
          roofH,
          eaveLen + overhang * 2,
          ridgeYaw,
          b.x,
          b.y + wallH,
          b.z,
        ),
      );
    }
    console.log(
      `[Buildings] extruded ${annBuildings.length} procedural buildings from annotations`,
    );
  }
}

function extractWindsockMarkers(
  terrain: TerrainAsset,
): Array<{ x: number; y: number; z: number }> {
  return terrain.worldObjects
    .filter((object) => object.type === "windsock")
    .map((object) => {
      const [x, z] = object.position;
      const y = Math.max(sampleTerrainHeight(terrain, x, z), 0.05);
      return { x, y, z };
    });
}
